import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Search, UserPlus, Check, Clock, UserCheck, MessageCircle } from "lucide-react";

export default function FriendsTab() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const { data: friends = [] } = useQuery({
    queryKey: ["friends"],
    queryFn: async () => (await api.get("/friends")).data,
  });
  const { data: requests = [] } = useQuery({
    queryKey: ["requests"],
    queryFn: async () => (await api.get("/friends/requests")).data,
    refetchInterval: 8000,
  });

  const doSearch = async (val) => {
    setQ(val);
    if (!val.trim()) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await api.get("/users/search", { params: { q: val } });
      setResults(res.data);
    } finally {
      setSearching(false);
    }
  };

  const sendReq = useMutation({
    mutationFn: (id) => api.post(`/friends/request/${id}`),
    onSuccess: () => {
      toast.success("Demande envoyée");
      doSearch(q);
      qc.invalidateQueries({ queryKey: ["friends"] });
    },
  });

  const accept = useMutation({
    mutationFn: (id) => api.post(`/friends/accept/${id}`),
    onSuccess: () => {
      toast.success("Ami ajouté");
      qc.invalidateQueries({ queryKey: ["requests"] });
      qc.invalidateQueries({ queryKey: ["friends"] });
    },
  });

  const decline = useMutation({
    mutationFn: (id) => api.post(`/friends/decline/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["requests"] }),
  });

  return (
    <div>
      <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-6">Amis</p>

      <div className="relative mb-8">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A8A8A]" />
        <input
          data-testid="friend-search-input"
          value={q}
          onChange={(e) => doSearch(e.target.value)}
          placeholder="Rechercher par nom ou email…"
          className="w-full bg-[#121212] border border-white/10 rounded-full pl-11 pr-5 py-3.5 text-sm placeholder:text-[#8A8A8A] focus:outline-none focus:ring-2 focus:ring-white/30 transition"
        />
      </div>

      {q && (
        <div className="mb-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-4">Résultats</p>
          {searching ? (
            <p className="text-[#8A8A8A] text-sm">Recherche…</p>
          ) : results.length === 0 ? (
            <p className="text-[#8A8A8A] text-sm">Aucun utilisateur trouvé.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {results.map((u) => (
                <div key={u.user_id} className="flex items-center gap-4 bg-[#121212] border border-white/5 rounded-2xl p-4">
                  <Avatar className="h-11 w-11 border border-white/10">
                    <AvatarImage src={u.picture} />
                    <AvatarFallback className="bg-[#1A1A1A] text-[#C4A47C]">{u.name?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{u.name}</p>
                    <p className="text-sm text-[#8A8A8A] truncate">{u.email}</p>
                  </div>
                  {u.status === "friends" ? (
                    <span className="text-xs text-[#C4A47C] flex items-center gap-1"><UserCheck className="h-4 w-4" /> Ami</span>
                  ) : u.status === "sent" ? (
                    <span className="text-xs text-[#8A8A8A] flex items-center gap-1"><Clock className="h-4 w-4" /> Envoyé</span>
                  ) : u.status === "incoming" ? (
                    <button
                      data-testid="friend-request-button"
                      onClick={() => sendReq.mutate(u.user_id)}
                      className="bg-white text-black text-sm font-medium px-4 py-2 rounded-full hover:bg-gray-200 transition flex items-center gap-1"
                    >
                      <Check className="h-4 w-4" /> Accepter
                    </button>
                  ) : (
                    <button
                      data-testid="friend-request-button"
                      onClick={() => sendReq.mutate(u.user_id)}
                      className="bg-[#1A1A1A] border border-white/10 text-sm font-medium px-4 py-2 rounded-full hover:bg-[#2A2A2A] transition flex items-center gap-1"
                    >
                      <UserPlus className="h-4 w-4" /> Ajouter
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {requests.length > 0 && (
        <div className="mb-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C4A47C] mb-4">
            Demandes ({requests.length})
          </p>
          <div className="flex flex-col gap-2">
            {requests.map((r) => (
              <div key={r.id} data-testid="friend-request-item" className="flex items-center gap-4 bg-[#121212] border border-[#C4A47C]/20 rounded-2xl p-4">
                <Avatar className="h-11 w-11 border border-white/10">
                  <AvatarImage src={r.from.picture} />
                  <AvatarFallback className="bg-[#1A1A1A] text-[#C4A47C]">{r.from.name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{r.from.name}</p>
                  <p className="text-sm text-[#8A8A8A] truncate">souhaite vous ajouter</p>
                </div>
                <button
                  data-testid="accept-request-button"
                  onClick={() => accept.mutate(r.from.user_id)}
                  className="bg-white text-black text-sm font-medium px-4 py-2 rounded-full hover:bg-gray-200 transition"
                >
                  Accepter
                </button>
                <button
                  onClick={() => decline.mutate(r.from.user_id)}
                  className="text-[#8A8A8A] text-sm hover:text-white transition px-2"
                >
                  Refuser
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-4">
          Mes amis ({friends.length})
        </p>
        {friends.length === 0 ? (
          <p className="text-[#8A8A8A] text-sm">Pas encore d'amis. Recherchez quelqu'un ci-dessus.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {friends.map((f) => (
              <button
                key={f.user_id}
                data-testid="friend-list-item"
                onClick={() => navigate(`/app/chat/${f.user_id}`)}
                className="w-full flex items-center gap-4 bg-[#121212] border border-white/5 rounded-2xl p-4 hover:bg-[#1A1A1A] hover:-translate-y-[1px] transition-all duration-300 text-left"
              >
                <Avatar className="h-11 w-11 border border-white/10">
                  <AvatarImage src={f.picture} />
                  <AvatarFallback className="bg-[#1A1A1A] text-[#C4A47C]">{f.name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{f.name}</p>
                    {f.in_heat && (
                      <span className="shrink-0 text-[10px] tracking-wide font-bold text-orange-400 bg-orange-500/10 border border-orange-500/30 rounded-full px-2 py-0.5">
                        🦁 En chaleur
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[#8A8A8A] truncate">{f.email}</p>
                </div>
                <MessageCircle className="h-4 w-4 text-[#8A8A8A] shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
